import React, { FC, useEffect, useMemo, useState } from "react";
import { FormikProps } from "formik";
import Select, { components } from "react-select";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

import { useAppDispatch } from "src/redux/storeHooks";
import { getCustomFieldValueListAsync } from "src/redux/actions/omsPageActions";

interface VariantCustomFieldsProps {
  values: any;
  setFormikState: FormikProps<any>["setFormikState"];
  mode: "create" | "edit";
  allCustomFields?: any[];
  editCustomFields?: any[];
}

const CheckboxOption = (props: any) => {
  const { isSelected, label } = props;

  return (
    <components.Option {...props}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <input
          type="checkbox"
          checked={isSelected}
          readOnly
          style={{ pointerEvents: "none" }}
        />
        <span>{label}</span>
      </div>
    </components.Option>
  );
};

const VariantCustomFields: FC<VariantCustomFieldsProps> = ({
  values,
  setFormikState,
  mode,
  allCustomFields = [],
  editCustomFields = [],
}) => {
  const dispatch = useAppDispatch();

  const [fieldsMeta, setFieldsMeta] = useState<any[]>([]);
  const [customFieldValuesById, setCustomFieldValuesById] = useState<
    Record<number, any[]>
  >({});
  const [loadingFieldValues, setLoadingFieldValues] = useState<
    Record<number, boolean>
  >({});

  const extractList = (res: any): any[] => {
    return (
      res?.data?.results?.data ||
      res?.results?.data ||
      res?.data?.data ||
      res?.data ||
      res?.results ||
      res ||
      []
    );
  };

  const getEditFieldMap = (items: any[] = []) => {
    const map = new Map<number, any[]>();

    items.forEach((item: any) => {
      const fieldId = Number(
        item?.platform_field ??
        item?.platform_field_id ??
        item?.custom_field_id ??
        item?.field_id ??
        item?.platform_field?.id ??
        item?.id
      );

      if (!fieldId) return;

      if (!map.has(fieldId)) {
        map.set(fieldId, []);
      }

      map.get(fieldId)!.push(item);
    });

    return map;
  };

  const parseCheckboxValue = (input: any): boolean => {
    if (typeof input === "boolean") return input;

    if (typeof input === "string") {
      const normalized = input.trim().toLowerCase();
      return normalized === "true" || normalized === "1" || normalized === "yes";
    }

    if (typeof input === "number") {
      return input === 1;
    }

    if (input && typeof input === "object") {
      const raw =
        input?.value ??
        input?.name ??
        input?.label ??
        input?.api_name ??
        input?.field_value;

      return parseCheckboxValue(raw);
    }

    return false;
  };

  const getEditFieldRawValue = (editFields: any[] = [], fieldMeta?: any) => {
    if (!editFields?.length) return undefined;

    const inputType = fieldMeta?.input_type;

    if (inputType === "SELECT") {
      const row = editFields[0];
      return row?.platform_field_data != null
        ? String(row.platform_field_data)
        : row?.platform_field_data_id != null
          ? String(row.platform_field_data_id)
          : undefined;
    }

    if (inputType === "MULTISELECT") {
      const ids = editFields
        .map((row: any) =>
          Number(
            row?.platform_field_data_id ??
            row?.platform_field_data ??
            row?.value_id
          )
        )
        .filter((n: number) => !Number.isNaN(n));

      return ids;
    }

    if (inputType === "CHECKBOX") {
      const row = editFields[0];
      const raw =
        row?.value ??
        row?.name ??
        row?.api_name ??
        row?.api_code ??
        row?.field_value ??
        row?.custom_field_value ??
        row?.platform_field_data;

      return parseCheckboxValue(raw);
    }

    const row = editFields[0];
    return (
      row?.value ??
      row?.api_name ??
      row?.api_code ??
      row?.field_value ??
      row?.custom_field_value ??
      undefined
    );
  };

  const normalizeInitialValue = (field: any, rawValue: any) => {
    const fallbackDefault =
      field?.default_value?.name ??
      field?.default_value?.value ??
      field?.default_value ??
      null;

    const sourceValue =
      rawValue !== undefined && rawValue !== null
        ? rawValue
        : fallbackDefault;

    switch (field?.input_type) {
      case "MULTISELECT": {
        // if edit value exists
        if (Array.isArray(sourceValue)) {
          return sourceValue
            .map((item: any) =>
              typeof item === "object"
                ? Number(item?.id ?? item?.value)
                : Number(item)
            )
            .filter((x: number) => !Number.isNaN(x));
        }

        // fallback from field.options having is_default
        if (Array.isArray(field?.options)) {
          return field.options
            .filter((opt: any) => opt?.is_default)
            .map((opt: any) => Number(opt?.id))
            .filter((x: number) => !Number.isNaN(x));
        }

        return [];
      }

      case "SELECT": {
        // if edit value exists
        if (Array.isArray(sourceValue) && sourceValue[0]) {
          return String(sourceValue[0]?.id ?? sourceValue[0]?.value ?? "");
        }

        if (sourceValue && typeof sourceValue === "object") {
          return String(sourceValue?.id ?? sourceValue?.value ?? "");
        }

        // if API sends default_value.name instead of id, try matching from field.options
        if (
          (sourceValue == null || sourceValue === "") &&
          field?.default_value?.id != null
        ) {
          return String(field.default_value.id);
        }

        if (field?.default_value?.id != null) {
          return String(field.default_value.id);
        }

        return sourceValue != null ? String(sourceValue) : "";
      }

      case "CHECKBOX": {
        return parseCheckboxValue(sourceValue);
      }

      case "NUMBER": {
        if (sourceValue == null || sourceValue === "") return "";
        if (typeof sourceValue === "object") {
          return sourceValue?.name ?? sourceValue?.value ?? "";
        }
        return sourceValue;
      }

      case "TEXT":
      case "MULTILINETEXT":
      case "EMAIL":
      case "URL":
      case "DATE":
      case "DATE_TIME":
      case "TIME": {
        if (sourceValue == null) return "";
        if (typeof sourceValue === "object") {
          return sourceValue?.name ?? sourceValue?.value ?? "";
        }
        return sourceValue;
      }

      default: {
        if (sourceValue == null) return "";
        if (typeof sourceValue === "object") {
          return sourceValue?.name ?? sourceValue?.value ?? "";
        }
        return sourceValue;
      }
    }
  };

  const mergeCustomFieldsForForm = (
    masterFields: any[] = [],
    variantFields: any[] = []
  ) => {
    const editMap = getEditFieldMap(variantFields);

    return masterFields.map((field: any) => {
      const matchedEditFields = editMap.get(Number(field.id)) || [];
      const rawEditValue = getEditFieldRawValue(matchedEditFields, field);

      return {
        ...field,
        initialValue: normalizeInitialValue(field, rawEditValue),
      };
    });
  };

  const updateCustomFieldValue = (id: number, value: any) => {
    setFormikState((prev: any) => ({
      ...prev,
      values: {
        ...prev.values,
        custom_fields: {
          ...(prev.values.custom_fields || {}),
          [id]: value,
        },
      },
    }));
  };

  useEffect(() => {
    const mergedFields = mergeCustomFieldsForForm(
      allCustomFields,
      mode === "edit" ? editCustomFields : []
    );

    setFieldsMeta(mergedFields);

    setFormikState((prev: any) => {
      const current = prev.values.custom_fields || {};
      const next = { ...current };

      mergedFields.forEach((field: any) => {
        if (next[field.id] === undefined) {
          next[field.id] = field.initialValue;
        }
      });

      return {
        ...prev,
        values: {
          ...prev.values,
          custom_fields: next,
        },
      };
    });
  }, [allCustomFields, editCustomFields, mode, setFormikState]);

  useEffect(() => {
    const selectableFields = (fieldsMeta || []).filter(
      (f: any) =>
        (f.input_type === "SELECT" || f.input_type === "MULTISELECT") &&
        f.isActive &&
        f.owner === "PRODUCTVARIANT"
    );

    if (!selectableFields.length) return;

    selectableFields.forEach((field: any) => {
      const fieldId = Number(field.id);

      if (!fieldId) return;
      if (customFieldValuesById[fieldId]?.length) return;
      if (loadingFieldValues[fieldId]) return;

      setLoadingFieldValues((prev) => ({ ...prev, [fieldId]: true }));

      dispatch(getCustomFieldValueListAsync({ id: fieldId }))
        .unwrap()
        .then((res: any) => {
          const list = extractList(res);

          const normalized = (list || [])
            .map((x: any) => ({
              id: x?.id ?? x?.value_id ?? x?.platform_value_id,
              name:
                x?.name ??
                x?.api_code ??
                x?.value ??
                x?.display_name ??
                x?.label,
            }))
            .filter((x: any) => x.id != null);

          setCustomFieldValuesById((prev) => ({
            ...prev,
            [fieldId]: normalized,
          }));
        })
        .catch((err: any) => {
          console.error(err);
        })
        .finally(() => {
          setLoadingFieldValues((prev) => ({ ...prev, [fieldId]: false }));
        });
    });
  }, [dispatch, fieldsMeta, customFieldValuesById, loadingFieldValues]);

  const hasActiveVariantFields = useMemo(() => {
    return (fieldsMeta || []).some(
      (f: any) => f.isActive && f.owner === "PRODUCTVARIANT"
    );
  }, [fieldsMeta]);

  const renderCustomFieldInput = (field: any) => {
    const value = values?.custom_fields?.[field.id];

    switch (field.input_type) {
      case "TEXT":
        return (
          <input
            type="text"
            className="form-control"
            value={value ?? ""}
            onChange={(e) => updateCustomFieldValue(field.id, e.target.value)}
          />
        );

      case "MULTILINETEXT":
        return (
          <textarea
            className="form-control"
            rows={4}
            value={value ?? ""}
            onChange={(e) => updateCustomFieldValue(field.id, e.target.value)}
          />
        );

      case "NUMBER":
        return (
          <input
            type="number"
            className="form-control"
            value={value ?? ""}
            onChange={(e) => updateCustomFieldValue(field.id, e.target.value)}
          />
        );

      case "EMAIL":
        return (
          <input
            type="email"
            className="form-control"
            value={value ?? ""}
            onChange={(e) => updateCustomFieldValue(field.id, e.target.value)}
          />
        );

      case "DATE":
        return (
          <input
            type="date"
            className="form-control"
            value={value ?? ""}
            onChange={(e) => updateCustomFieldValue(field.id, e.target.value)}
          />
        );

      case "DATE_TIME":
        return (
          <input
            type="datetime-local"
            className="form-control"
            value={value ?? ""}
            onChange={(e) => updateCustomFieldValue(field.id, e.target.value)}
          />
        );

      case "TIME":
        return (
          <input
            type="time"
            className="form-control"
            value={value ?? ""}
            onChange={(e) => updateCustomFieldValue(field.id, e.target.value)}
          />
        );

      case "URL":
        return (
          <input
            type="url"
            className="form-control"
            value={value ?? ""}
            onChange={(e) => updateCustomFieldValue(field.id, e.target.value)}
            placeholder="https://..."
          />
        );

      case "CHECKBOX":
        return (
          <input
            type="checkbox"
            checked={!!value}
            onChange={(e) => updateCustomFieldValue(field.id, e.target.checked)}
          />
        );

      case "SELECT": {
        const options = (customFieldValuesById[field.id] || []).map((o: any) => ({
          value: String(o.id),
          label: o.name,
        }));

        const selected =
          options.find((o: any) => String(o.value) === String(value ?? "")) ||
          null;

        return (
          <Select
            classNamePrefix="rs"
            options={options}
            value={selected}
            onChange={(opt: any) =>
              updateCustomFieldValue(field.id, opt?.value || "")
            }
            placeholder="Select..."
            isClearable
            menuPortalTarget={document.body}
            menuPosition="fixed"
            maxMenuHeight={260}
            menuShouldScrollIntoView={false}
          />
        );
      }

      case "MULTISELECT": {
        const options = (customFieldValuesById[field.id] || []).map((o: any) => ({
          value: Number(o.id),
          label: o.name,
        }));

        const selected = options.filter((o: any) =>
          Array.isArray(value) ? value.includes(o.value) : false
        );

        return (
          <Select
            classNamePrefix="rs"
            isMulti
            options={options}
            value={selected}
            onChange={(items: any) =>
              updateCustomFieldValue(
                field.id,
                (items || []).map((x: any) => x.value)
              )
            }
            placeholder="Select..."
            components={{ Option: CheckboxOption }}
            closeMenuOnSelect={false}
            hideSelectedOptions={false}
            menuPortalTarget={document.body}
            menuPosition="fixed"
            menuPlacement="auto"
            maxMenuHeight={260}
            menuShouldScrollIntoView={false}
          />
        );
      }

      default:
        return (
          <input
            type="text"
            className="form-control"
            value={value ?? ""}
            onChange={(e) => updateCustomFieldValue(field.id, e.target.value)}
          />
        );
    }
  };

  if (!hasActiveVariantFields) return null;

  return (
    <div className="card card-flush">
      <div className="card-headerP d-flex justify-content-between align-items-center">
        <div className="card-titleP">
          <h2 className="mb-0">Custom Fields</h2>
        </div>
      </div>

      <div className="card-bodyP pt-0 mt-2">
        <div className="cfGrid">
          {fieldsMeta
            .filter((f: any) => f.isActive && f.owner === "PRODUCTVARIANT")
            .map((field: any) => {
              const tooltipText =
                field?.description ||
                field?.help_text ||
                field?.api_description ||
                field?.notes ||
                "-";

              return (
                <div key={field.id} className="cfTile">
                  <div className="cfTile__head">
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="fw-bold text-primary d-flex align-items-center gap-3">
                        {field.display_name}
                        {field.isRequired ? (
                          <span className="text-danger">*</span>
                        ) : null}

                        <OverlayTrigger
                          placement="top"
                          overlay={<Tooltip>{tooltipText}</Tooltip>}
                        >
                          <span className="cfInfoIcon">
                            <i className="fa fa-info-circle" />
                          </span>
                        </OverlayTrigger>
                      </div>
                    </div>
                  </div>

                  <div className="cfTile__body">{renderCustomFieldInput(field)}</div>
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
};

export default VariantCustomFields;   